import { setSelectedProject } from '@/state/slices';
import { useAppSelector } from '@/state';
import { PermissionCheck } from '@/components';
import { permissionsKeys } from '@/types/permissionsType';
import { ProjectsApi } from '@api';
import { Box, Card, Input, Span, Stack, Text, VStack } from '@chakra-ui/react';
import {
  FormController,
  FormInput,
  formSchemas,
  FormTextArea,
  RenderFormElement,
} from '@components/form';
import { ProjectsType } from '@types';
import { CheckBoxGroup, OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

interface Props {
  defaultProject?: ProjectsType.ProjectPartial;
  isNew?: boolean;
}

type UserName = ProjectsType.ProjectMemberOption;
const EMPTY_MEMBER_OPTIONS: UserName[] = [];

interface ProjectMembershipFieldProps {
  selectedMemberIds: number[];
  onChange: (memberIds: number[]) => void;
  onOptionsLoaded: (users: UserName[]) => void;
}

export const formatProjectMembers = (
  memberIds: number[],
  users: UserName[],
) => {
  if (memberIds.length === 0) return 'No members';

  return memberIds
    .map((memberId) => {
      const user = users.find(({ id }) => id === memberId);
      return `${user?.display_name ?? 'User'} (ID: ${memberId})`;
    })
    .join(', ');
};

export const ProjectMembershipField: React.FC<ProjectMembershipFieldProps> = ({
  selectedMemberIds,
  onChange,
  onOptionsLoaded,
}) => {
  const [search, setSearch] = useState('');
  const { data: users, isLoading } =
    ProjectsApi.Api.useGetProjectMemberOptionsQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const memberOptions = users ?? EMPTY_MEMBER_OPTIONS;

  useEffect(() => {
    if (users) {
      onOptionsLoaded(users);
    }
  }, [onOptionsLoaded, users]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return memberOptions;

    return memberOptions.filter(({ display_name }) =>
      display_name.toLowerCase().includes(normalizedSearch),
    );
  }, [memberOptions, search]);

  const handleSelection = (visibleSelectedIds: number[]) => {
    const visibleIds = new Set(filteredUsers.map(({ id }) => id));
    const hiddenSelectedIds = selectedMemberIds.filter(
      (memberId) => !visibleIds.has(memberId),
    );
    onChange(
      Array.from(new Set([...hiddenSelectedIds, ...visibleSelectedIds])).sort(
        (first, second) => first - second,
      ),
    );
  };

  return (
    <Box pt={2}>
      <Input
        aria-label="Search project members"
        placeholder="Search project members"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        mb={3}
      />
      <CheckBoxGroup
        key={`${search}-${selectedMemberIds.join('-')}`}
        label="Project Members"
        infoTip="Select users who may access this project"
        items={filteredUsers}
        keyForDisplay="display_name"
        keyForValue="id"
        multiple={true}
        useSelectAllOption={true}
        layout="stack"
        maxHeight="16rem"
        isLoading={isLoading}
        defaultValue={selectedMemberIds}
        onSelect={handleSelection}
        createDescription={(user: UserName) => `User ID: ${user.id}`}
      />
    </Box>
  );
};

const Inner_ManageCard: React.FC<Props> = ({
  defaultProject,
  isNew,
}: Props) => {
  const formSchema = isNew ? projectSchema : projectSchemaPartial;
  type FormData = z.infer<typeof formSchema>;

  const [patchProject, { isSuccess: isPatchProjectSuccess }] =
    ProjectsApi.Api.usePatchProjectByIdMutation();
  const [postProject, { isSuccess: isPostProjectSuccess }] =
    ProjectsApi.Api.usePostProjectMutation();
  const [serverCodeError, setServerCodeError] = useState<string | null>(null);
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>(
    defaultProject?.member_ids ?? [],
  );
  const [memberOptions, setMemberOptions] = useState<UserName[]>([]);
  const canManageProjectAccess = useAppSelector((state) =>
    (state.auth.current.user?.permissions ?? []).includes(
      permissionsKeys.manage_project_access,
    ),
  );

  const dispatch = useDispatch();

  const onFormSubmit = async (formData: FormData) => {
    setServerCodeError(null);
    OverlayDialog.open('confirmProjectUpdate', {
      overlayId: 'confirmProjectUpdate',
      title: `Confirm ${isNew ? 'Project Creation' : `Project Update`}`,
      description: `Are you sure you want to ${
        isNew ? 'create' : 'update'
      } the project?`,
      content: (
        <VStack align="flex-start" gap={2}>
          {!isNew && (
            <Text>
              Project :{' '}
              <Span
                textStyle={'heading.card'}
                color={'tertiary.fg'}
              >{`${defaultProject?.name}`}</Span>
            </Text>
          )}
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProject?.name || undefined
            }
            currentValue={formData.name}
            label="Project Name"
          />
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProject?.code || undefined
            }
            currentValue={formData.code}
            label="Project Code"
          />
          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProject?.contractor || undefined
            }
            currentValue={formData.contractor}
            label="Contractor"
          />

          <RenderFormElement
            previousValue={
              isNew ? undefined : defaultProject?.description || undefined
            }
            currentValue={formData.description}
            label="Project Description"
          />
          <RenderFormElement
            previousValue={
              isNew
                ? undefined
                : dayjs(defaultProject?.start_date).toDate().toDateString() ||
                  undefined
            }
            currentValue={dayjs(formData.start_date).toDate().toDateString()}
            label="Project Start Date"
          />
          {canManageProjectAccess && (
            <RenderFormElement
              previousValue={
                isNew
                  ? undefined
                  : formatProjectMembers(
                      defaultProject?.member_ids ?? [],
                      memberOptions,
                    )
              }
              currentValue={formatProjectMembers(
                selectedMemberIds,
                memberOptions,
              )}
              label="Project Members"
            />
          )}
        </VStack>
      ),
      handleConfirm: async () => {
        const projectMembership = canManageProjectAccess
          ? { member_ids: selectedMemberIds }
          : {};
        const projectData = {
          ...(formData.name && { name: formData.name }),
          ...(formData.code && { code: formData.code }),
          ...(formData.description && {
            description: formData.description || '',
          }),
          ...(formData.contractor && { contractor: formData.contractor }),
          ...(formData.start_date && {
            start_date: dayjs(formData.start_date).format('YYYY-MM-DD'),
          }),
          ...projectMembership,
        };

        try {
          const data = isNew
            ? await postProject({
                name: formData.name ?? '',
                code: formData.code ?? '',
                description: formData.description ?? '',
                contractor: formData.contractor ?? '',
                start_date:
                  formData.start_date?.toString() ??
                  dayjs().toDate().toISOString(),
                ...projectMembership,
              }).unwrap()
            : await patchProject({
                projectId: defaultProject!.id!,
                body: projectData,
              }).unwrap();

          if (isNew) {
            setSelectedMemberIds([]);
          }

          if (!isNew) {
            dispatch(
              setSelectedProject({
                id: data.id!,
                item: data,
              }),
            );
          }

          toasterApi({
            type: 'success',
            status: isNew ? 201 : 200,
            code: 'Success',
            message: `${isNew ? 'Created' : 'Updated'} Project`,
          });
        } catch (error: any) {
          const codeError = error?.data?.code;
          const message = Array.isArray(codeError)
            ? codeError.join(' ')
            : codeError ||
              error?.data?.detail ||
              error?.data?.non_field_errors?.join?.(' ') ||
              `Could not ${isNew ? 'create' : 'update'} project`;
          setServerCodeError(String(message));
          toasterApi({
            type: 'error',
            status: error?.status ?? 400,
            code: 'ProjectValidationError',
            message: String(message),
          });
        }
      },
    });
  };

  return (
    <>
      <Card.Root width="100%" variant="elevated">
        <Card.Body gap="7">
          <FormController
            key={`${defaultProject?.id}-${isNew}-form-${isPatchProjectSuccess}-${isPostProjectSuccess}`}
            label={isNew ? 'Create Project' : 'Update Project Details'}
            formSchema={formSchema}
            helperText={`Please fill out the form to ${
              isNew ? 'create a new project' : 'update the project details'
            }.`}
            onFormSubmit={onFormSubmit}
            defaultValues={{
              name: isNew ? undefined : defaultProject?.name || '',
              code: isNew ? undefined : defaultProject?.code || '',
              description: isNew
                ? undefined
                : defaultProject?.description || '',
              contractor: isNew ? undefined : defaultProject?.contractor || '',
              start_date: isNew ? undefined : defaultProject?.start_date,
            }}
            buttonProps={{
              variant: 'outline',
              color: 'tertiary.fg',
              width: '100%',
              mt: '4',
            }}
          >
            {({
              register,
              control,
              errors,
            }: {
              register: UseFormRegister<FormData>;
              control: Control<FormData>;
              errors: FieldErrors<FormData>;
            }) => {
              return (
                <>
                  <FormInput
                    control={control}
                    label="Project Name"
                    id="name"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.projectSchema.nameHelper}
                    required={isNew}
                  />
                  <FormInput
                    control={control}
                    label="Project Code"
                    id="code"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.projectSchema.codeHelper}
                    required
                  />
                  {serverCodeError && (
                    <Text color="fg.error" fontSize="sm">
                      {serverCodeError}
                    </Text>
                  )}
                  <FormTextArea
                    control={control}
                    label="Project Description"
                    id="description"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.projectSchema.descriptionHelper}
                  />
                  <FormInput
                    control={control}
                    label="Contractor"
                    id="contractor"
                    register={register}
                    errors={errors}
                    helperText="Contractor / GC name"
                  />
                  <FormInput
                    control={control}
                    label="Project Start Date"
                    id="start_date"
                    register={register}
                    errors={errors}
                    helperText={formSchemas.releaseSchema.dateHelper}
                    type="date"
                    required={isNew}
                  />
                  <PermissionCheck
                    permissions={[permissionsKeys.manage_project_access]}
                  >
                    <ProjectMembershipField
                      selectedMemberIds={selectedMemberIds}
                      onChange={setSelectedMemberIds}
                      onOptionsLoaded={setMemberOptions}
                    />
                  </PermissionCheck>
                </>
              );
            }}
          </FormController>
        </Card.Body>
      </Card.Root>
    </>
  );
};

const ManageCard: React.FC<Props> = ({
  defaultProject,
  isNew = false,
}: Props) => {
  return (
    <Stack>
      <Inner_ManageCard defaultProject={defaultProject} isNew={isNew} />
    </Stack>
  );
};

export default ManageCard;

export const projectSchema = z.object({
  name: formSchemas.projectSchema.nameSchema,
  code: formSchemas.projectSchema.codeSchema,
  contractor: z.string().optional().nullable(),
  description: z.string().optional(),
  start_date: z.string().nonempty({ error: 'Start date is required' }),
});

export const projectSchemaPartial = projectSchema.partial({
  name: true,
  code: true,
  contractor: true,
  description: true,
  start_date: true,
});
